import { PlusCircleFilled } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Col,
  Input,
  List,
  Row,
  Space,
  Spin,
  notification,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  CREATE_MODE,
  EDIT_MODE,
  INITIAL_MODE,
  NOTIFICATION_CREATED,
  NOTIFICATION_UPDATED,
} from '../app/constants';
import {
  filterDataFromKeyword,
  range,
  sortByKey,
} from '../components/util/Helper';
import {
  selectSelectedService,
  selectServiceList,
  selectServiceLoadingStatus,
} from '../features/service/serviceSelector';
import {
  createService,
  fetchServiceDetail,
  fetchServiceList,
  removeService,
  updateService,
} from '../features/service/serviceSlice';
import { fetchStaffList, selectStaffList } from '../features/staff/staffSlice';
import { parseApiResponseAndSendNotification } from '../handlers/ApiErrorNotifier';
import { logHandler } from '../handlers/LogHandler';
import {
  BigScissorIcon,
  SearchIcon,
  ServicePlaceholderIcon,
  ServiceForm,
  DefaultPageLayout,
} from './../components/index';

function ServicePage() {
  const [loading, setLoading] = useState(false);

  const services = useSelector(selectServiceList);
  const service = useSelector(selectSelectedService);
  const selectorLoadingStatus = useSelector(selectServiceLoadingStatus);

  const [serviceList, setServiceList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [mode, setMode] = useState(INITIAL_MODE);
  const dispatch = useDispatch();
  const allStaff = useSelector(selectStaffList);

  const { t } = useTranslation();

  const fetchApiData = useCallback(() => {
    if (!allStaff.length) {
      dispatch(fetchStaffList());
    }
    dispatch(fetchServiceList());
  }, [allStaff, dispatch]);

  useEffect(() => fetchApiData(), [fetchApiData]);

  useEffect(() => {
    let keyword = searchKeyword.length ? searchKeyword.toLowerCase() : '';
    let filteredClients = filterDataFromKeyword(services, keyword, ['title']);
    filteredClients = sortByKey(filteredClients, 'title');
    setServiceList(filteredClients);
  }, [searchKeyword, services]);

  const onSearch = (event) => setSearchKeyword(event.target.value);

  const emptyContent = (
    <Row type="flex" align="center" className="w-100 vh-100 text-center">
      <Col span={24}>
        <div style={{ marginTop: 'calc(30vh - 85px)' }}>
          <img src={BigScissorIcon} alt="Servizi" />
          <p className="text-center mt-4 lead">{t('servicepage.empty_desc')}</p>
        </div>
      </Col>
    </Row>
  );

  const addService = () => {
    setMode(INITIAL_MODE);
    if (!allStaff.length) {
      dispatch(fetchStaffList())
        .unwrap()
        .finally(() => {
          setMode(CREATE_MODE);
        });
      return;
    }
    setMode(CREATE_MODE);
  };

  const updateOrCreateService = (data) => {
    setLoading(true);
    let staff_ids = Object.keys(data.staff);
    let title = data.name ?? service.title;
    staff_ids = staff_ids.filter((x) => data.staff[x]);
    const apiFormat = {
      title: title,
      description: data.description || ' ',
      only_service: data.only_service || false,
      execution_time: parseInt(data.hour) * 60 + parseInt(data.minute),
      service_ids: [data.service_id],
      staff_ids: staff_ids,
      service_identifier: title.substr(0, 1).toUpperCase(),
      price: data.price,
      color: data.color,
      image: data.image,
    };

    const request =
      mode !== CREATE_MODE
        ? dispatch(updateService({ apiFormat, id: service.id }))
        : dispatch(createService(apiFormat));
    request
      .unwrap()
      .then((data) => {
        if (data.status === 422) {
          parseApiResponseAndSendNotification(data);
          return;
        }
        parseApiResponseAndSendNotification(
          { status: 200 },
          mode !== CREATE_MODE ? NOTIFICATION_UPDATED : NOTIFICATION_CREATED,
        );
        fetchApiData();
        if (mode === CREATE_MODE) {
          selectService(data);
        }
      })
      .catch((err) => logHandler.error(err))
      .finally(() => setLoading(false));
  };

  const selectService = (service) => {
    dispatch(fetchServiceDetail(service))
      .unwrap()
      .then((res) => {
        if (res.status > 300) {
          notification['error']({
            message: res.data.error,
          });
          return res;
        }
        setMode(EDIT_MODE);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onHandleDeleteService = () => {
    dispatch(removeService(service))
      .unwrap()
      .finally((res) => {
        setMode(INITIAL_MODE);
      });
  };

  return (
    <DefaultPageLayout
      loadingContent={loading}
      loadingSidebar={selectorLoadingStatus.includes('fetchServiceList')}
    >
      <DefaultPageLayout.Title>
        <div className="page-title-ag">
          <span style={{ paddingRight: '12px' }}>
            <svg
              style={{ position: 'relative', top: '4px' }}
              width="26"
              height="24"
              viewBox="0 0 18 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.67219 12.4569C3.67219 12.8646 3.98826 13.1962 4.37674 13.1962C4.76527 13.1962 5.08135 12.8646 5.08135 12.4569C5.08135 12.0493 4.76527 11.7176 4.37674 11.7176C3.98826 11.7176 3.67219 12.0493 3.67219 12.4569ZM3.67219 4.54308C3.67219 4.95072 3.98826 5.28237 4.37674 5.28237C4.76527 5.28237 5.08135 4.95072 5.08135 4.54308C5.08135 4.13545 4.76527 3.80381 4.37674 3.80381C3.98826 3.80381 3.67219 4.13545 3.67219 4.54308ZM17 4.08924V4.08926C17 4.36338 16.8811 4.61613 16.6737 4.78268L12.0455 8.50001L16.6737 12.2173C16.881 12.3838 17 12.6366 17 12.9107C17 13.1013 16.9391 13.2896 16.8286 13.4411L16.7983 13.4826C16.0213 14.5477 14.5633 14.7509 13.5482 13.9355L9.41312 10.6143L7.72198 11.9726C7.74297 12.1328 7.75356 12.2947 7.75356 12.4569C7.75356 14.4106 6.23871 16 4.37674 16C2.51481 16 1 14.4106 1 12.4569C1 10.5033 2.51481 8.91383 4.37674 8.91383C4.88349 8.91383 5.37984 9.03299 5.83295 9.26127L6.78075 8.50001L5.83295 7.73873C5.37984 7.96701 4.88349 8.08619 4.37674 8.08619C2.51481 8.08619 1 6.49676 1 4.54308C1 2.58941 2.51481 1 4.37674 1C6.23871 1 7.75356 2.58941 7.75356 4.54308C7.75356 4.70536 7.74297 4.86724 7.72198 5.02741L9.41312 6.38571L13.5482 3.06446C14.5633 2.24912 16.0213 2.45233 16.7984 3.51742L16.8286 3.55887C16.9391 3.71035 17 3.8987 17 4.08926V4.08924ZM3.09515 12.4569C3.09515 11.7154 3.67006 11.1122 4.37674 11.1122C5.08344 11.1122 5.65839 11.7154 5.65839 12.4569C5.65839 13.1984 5.08344 13.8017 4.37674 13.8017C3.67006 13.8017 3.09515 13.1984 3.09515 12.4569ZM3.09515 4.54308C3.09515 3.80158 3.67006 3.19836 4.37674 3.19836C5.08344 3.19836 5.65839 3.80158 5.65839 4.54308C5.65839 5.28457 5.08344 5.88783 4.37674 5.88783C3.67006 5.88783 3.09515 5.28457 3.09515 4.54308ZM16.4229 4.08931C16.4229 4.0326 16.4058 3.97543 16.3704 3.92695L16.3402 3.88546C15.7556 3.08417 14.6626 2.93185 13.8989 3.54523L9.41312 7.1482L7.08722 5.28005C7.14545 5.04445 7.1765 4.7975 7.1765 4.54308C7.1765 2.9233 5.92054 1.60548 4.37674 1.60548C2.83299 1.60548 1.57702 2.9233 1.57702 4.54308C1.57702 6.16291 2.83299 7.48073 4.37674 7.48073C4.93131 7.48073 5.44825 7.30999 5.88365 7.01696L7.73009 8.50001L5.88365 9.98304C5.44825 9.69003 4.93131 9.51929 4.37674 9.51929C2.83299 9.51929 1.57702 10.8371 1.57702 12.4569C1.57702 14.0767 2.83299 15.3945 4.37674 15.3945C5.92054 15.3945 7.1765 14.0767 7.1765 12.4569C7.1765 12.2025 7.14545 11.9556 7.08722 11.72L9.41312 9.8518L13.8989 13.4548C14.6626 14.0681 15.7556 13.9158 16.3402 13.1145L16.3704 13.0731C16.4058 13.0246 16.4229 12.9674 16.4229 12.9107C16.4229 12.8303 16.3885 12.7508 16.323 12.6981L11.0962 8.50001L16.323 4.30189C16.3885 4.24921 16.423 4.16972 16.4229 4.08931Z"
                fill="#B19499"
              />
              <path
                d="M17 4.08926L16.75 4.08928V4.08926H17ZM16.6737 4.78268L16.5171 4.58777L16.5171 4.58776L16.6737 4.78268ZM12.0455 8.50001L11.8889 8.69492L11.6463 8.50001L11.8889 8.3051L12.0455 8.50001ZM16.6737 12.2173L16.5172 12.4123L16.5172 12.4123L16.6737 12.2173ZM16.8286 13.4411L17.0306 13.5884L17.0306 13.5885L16.8286 13.4411ZM16.7983 13.4826L16.5964 13.3353L16.5964 13.3353L16.7983 13.4826ZM13.5482 13.9355L13.3916 14.1305L13.3916 14.1305L13.5482 13.9355ZM9.41312 10.6143L9.25657 10.4194L9.41312 10.2937L9.56968 10.4194L9.41312 10.6143ZM7.72198 11.9726L7.4741 12.0051L7.45584 11.8657L7.56543 11.7777L7.72198 11.9726ZM5.83295 9.26127L5.9895 9.45619L5.8641 9.55691L5.72046 9.48454L5.83295 9.26127ZM6.78075 8.50001L6.9373 8.3051L7.17997 8.50001L6.9373 8.69492L6.78075 8.50001ZM5.83295 7.73873L5.72046 7.51546L5.8641 7.44309L5.9895 7.54381L5.83295 7.73873ZM7.72198 5.02741L7.56543 5.22232L7.45584 5.1343L7.4741 4.99493L7.72198 5.02741ZM9.41312 6.38571L9.56968 6.58062L9.41313 6.70636L9.25657 6.58062L9.41312 6.38571ZM13.5482 3.06446L13.3916 2.86954L13.3916 2.86954L13.5482 3.06446ZM16.7984 3.51742L16.5964 3.66478L16.5964 3.66477L16.7984 3.51742ZM16.8286 3.55887L16.6267 3.70623L16.6266 3.70623L16.8286 3.55887ZM17 4.08926H17.25L16.75 4.08929L17 4.08926ZM16.3704 3.92695L16.5724 3.77955L16.5724 3.77957L16.3704 3.92695ZM16.3402 3.88546L16.1382 4.03285L16.1382 4.0328L16.3402 3.88546ZM13.8989 3.54523L13.7424 3.35032L13.8989 3.54523ZM9.41312 7.1482L9.56968 7.34311L9.41312 7.46886L9.25657 7.34311L9.41312 7.1482ZM7.08722 5.28005L6.93067 5.47496L6.80623 5.37502L6.84452 5.22007L7.08722 5.28005ZM5.88365 7.01696L5.74406 6.80955L5.89673 6.70681L6.0402 6.82204L5.88365 7.01696ZM7.73009 8.50001L7.88665 8.3051L8.12932 8.50001L7.88665 8.69493L7.73009 8.50001ZM5.88365 9.98304L6.0402 10.178L5.89673 10.2932L5.74407 10.1905L5.88365 9.98304ZM7.08722 11.72L6.84452 11.78L6.80623 11.625L6.93067 11.5251L7.08722 11.72ZM9.41312 9.8518L9.25657 9.65689L9.41312 9.53114L9.56968 9.65689L9.41312 9.8518ZM13.8989 13.4548L13.7424 13.6497V13.6497L13.8989 13.4548ZM16.3402 13.1145L16.1382 12.9672L16.1383 12.9671L16.3402 13.1145ZM16.3704 13.0731L16.5724 13.2204L16.5723 13.2205L16.3704 13.0731ZM16.323 12.6981L16.1665 12.8931L16.1664 12.893L16.323 12.6981ZM11.0962 8.50001L10.9396 8.69492L10.6969 8.50001L10.9396 8.3051L11.0962 8.50001ZM16.323 4.30189L16.4795 4.49678L16.4795 4.4968L16.323 4.30189ZM3.92219 12.4569C3.92219 12.7379 4.13752 12.9462 4.37674 12.9462V13.4462C3.839 13.4462 3.42219 12.9912 3.42219 12.4569H3.92219ZM4.37674 12.9462C4.61602 12.9462 4.83135 12.7379 4.83135 12.4569H5.33135C5.33135 12.9912 4.91451 13.4462 4.37674 13.4462V12.9462ZM4.83135 12.4569C4.83135 12.1759 4.616 11.9676 4.37674 11.9676V11.4676C4.91453 11.4676 5.33135 11.9227 5.33135 12.4569H4.83135ZM4.37674 11.9676C4.13754 11.9676 3.92219 12.1759 3.92219 12.4569H3.42219C3.42219 11.9227 3.83898 11.4676 4.37674 11.4676V11.9676ZM3.92219 4.54308C3.92219 4.82413 4.13753 5.03237 4.37674 5.03237V5.53237C3.83898 5.53237 3.42219 5.07732 3.42219 4.54308H3.92219ZM4.37674 5.03237C4.61601 5.03237 4.83135 4.82411 4.83135 4.54308H5.33135C5.33135 5.07734 4.91452 5.53237 4.37674 5.53237V5.03237ZM4.83135 4.54308C4.83135 4.26207 4.61602 4.05381 4.37674 4.05381V3.55381C4.91451 3.55381 5.33135 4.00882 5.33135 4.54308H4.83135ZM4.37674 4.05381C4.13752 4.05381 3.92219 4.26205 3.92219 4.54308H3.42219C3.42219 4.00884 3.83899 3.55381 4.37674 3.55381V4.05381ZM17.25 4.08924V4.08926H16.75V4.08924H17.25ZM17.25 4.08924C17.25 4.43647 17.0987 4.76195 16.8302 4.9776L16.5171 4.58776C16.6634 4.47031 16.75 4.29028 16.75 4.08928L17.25 4.08924ZM16.8302 4.97759L12.202 8.69492L11.8889 8.3051L16.5171 4.58777L16.8302 4.97759ZM12.202 8.3051L16.8303 12.0224L16.5172 12.4123L11.8889 8.69492L12.202 8.3051ZM16.8303 12.0224C17.0987 12.238 17.25 12.5635 17.25 12.9107H16.75C16.75 12.7097 16.6633 12.5297 16.5172 12.4123L16.8303 12.0224ZM17.25 12.9107C17.25 13.1533 17.1729 13.3934 17.0306 13.5884L16.6267 13.2937C16.7054 13.1858 16.75 13.0492 16.75 12.9107H17.25ZM17.0306 13.5885L17.0003 13.63L16.5964 13.3353L16.6267 13.2937L17.0306 13.5885ZM17.0003 13.63C16.1411 14.8077 14.5204 15.0371 13.3916 14.1305L13.7047 13.7406C14.6062 14.4647 15.9015 14.2877 16.5964 13.3353L17.0003 13.63ZM13.3916 14.1305L9.25657 10.8092L9.56968 10.4194L13.7047 13.7406L13.3916 14.1305ZM9.56968 10.8092L7.87854 12.1675L7.56543 11.7777L9.25657 10.4194L9.56968 10.8092ZM7.96987 11.9401C7.99226 12.111 8.00356 12.2838 8.00356 12.4569H7.50356C7.50356 12.3055 7.49368 12.1545 7.4741 12.0051L7.96987 11.9401ZM8.00356 12.4569C8.00356 14.5372 6.38797 16.25 4.37674 16.25V15.75C6.08946 15.75 7.50356 14.284 7.50356 12.4569H8.00356ZM4.37674 16.25C2.36555 16.25 0.75 14.5372 0.75 12.4569H1.25C1.25 14.284 2.66407 15.75 4.37674 15.75V16.25ZM0.75 12.4569C0.75 10.3767 2.36554 8.66383 4.37674 8.66383V9.16383C2.66407 9.16383 1.25 10.6299 1.25 12.4569H0.75ZM4.37674 8.66383C4.92298 8.66383 5.45781 8.79234 5.94543 9.03801L5.72046 9.48454C5.30187 9.27365 4.84401 9.16383 4.37674 9.16383V8.66383ZM5.67639 9.06636L6.62419 8.3051L6.9373 8.69492L5.9895 9.45619L5.67639 9.06636ZM6.62419 8.69492L5.67639 7.93364L5.9895 7.54381L6.9373 8.3051L6.62419 8.69492ZM5.94543 7.96199C5.45782 8.20766 4.92299 8.33619 4.37674 8.33619V7.83619C4.844 7.83619 5.30187 7.72636 5.72046 7.51546L5.94543 7.96199ZM4.37674 8.33619C2.36554 8.33619 0.75 6.62336 0.75 4.54308H1.25C1.25 6.37015 2.66407 7.83619 4.37674 7.83619V8.33619ZM0.75 4.54308C0.75 2.4628 2.36555 0.75 4.37674 0.75V1.25C2.66407 1.25 1.25 2.71602 1.25 4.54308H0.75ZM4.37674 0.75C6.38797 0.75 8.00356 2.4628 8.00356 4.54308H7.50356C7.50356 2.71602 6.08946 1.25 4.37674 1.25V0.75ZM8.00356 4.54308C8.00356 4.71623 7.99226 4.88896 7.96987 5.05988L7.4741 4.99493C7.49368 4.84552 7.50356 4.69449 7.50356 4.54308H8.00356ZM7.87854 4.83249L9.56968 6.19079L9.25657 6.58062L7.56543 5.22232L7.87854 4.83249ZM9.25657 6.1908L13.3916 2.86954L13.7047 3.25937L9.56968 6.58062L9.25657 6.1908ZM13.3916 2.86954C14.5204 1.96291 16.1411 2.19234 17.0003 3.37008L16.5964 3.66477C15.9015 2.71233 14.6062 2.53532 13.7047 3.25937L13.3916 2.86954ZM17.0003 3.37006L17.0306 3.41151L16.6266 3.70623L16.5964 3.66478L17.0003 3.37006ZM17.0306 3.41151C17.1729 3.60654 17.25 3.84667 17.25 4.08924L16.75 4.08929C16.75 3.95073 16.7054 3.81417 16.6267 3.70623L17.0306 3.41151ZM16.75 4.08926V4.08924H17.25V4.08926H16.75ZM2.84515 12.4569C2.84515 11.5888 3.52079 10.8622 4.37674 10.8622V11.3622C3.81933 11.3622 3.34515 11.842 3.34515 12.4569H2.84515ZM4.37674 10.8622C5.2327 10.8622 5.90839 11.5888 5.90839 12.4569H5.40839C5.40839 11.842 4.93418 11.3622 4.37674 11.3622V10.8622ZM5.90839 12.4569C5.90839 13.325 5.2327 14.0517 4.37674 14.0517V13.5517C4.93419 13.5517 5.40839 13.0718 5.40839 12.4569H5.90839ZM4.37674 14.0517C3.52079 14.0517 2.84515 13.325 2.84515 12.4569H3.34515C3.34515 13.0718 3.81933 13.5517 4.37674 13.5517V14.0517ZM2.84515 4.54308C2.84515 3.67497 3.5208 2.94836 4.37674 2.94836V3.44836C3.81932 3.44836 3.34515 3.92818 3.34515 4.54308H2.84515ZM4.37674 2.94836C5.23269 2.94836 5.90839 3.67496 5.90839 4.54308H5.40839C5.40839 3.9282 4.93419 3.44836 4.37674 3.44836V2.94836ZM5.90839 4.54308C5.90839 5.41118 5.2327 6.13783 4.37674 6.13783V5.63783C4.93418 5.63783 5.40839 5.15796 5.40839 4.54308H5.90839ZM4.37674 6.13783C3.52079 6.13783 2.84515 5.41117 2.84515 4.54308H3.34515C3.34515 5.15797 3.81933 5.63783 4.37674 5.63783V6.13783ZM16.1729 4.08931C16.1729 4.08269 16.171 4.07774 16.1685 4.07432L16.5724 3.77957C16.6407 3.87312 16.6729 3.98251 16.6729 4.08931H16.1729ZM16.1685 4.07434L16.1382 4.03285L16.5421 3.73806L16.5724 3.77955L16.1685 4.07434ZM16.1382 4.0328C15.6358 3.34417 14.7055 3.21806 14.0555 3.74014L13.7424 3.35032C14.6197 2.64565 15.8754 2.82417 16.5421 3.73812L16.1382 4.0328ZM14.0555 3.74014L9.56968 7.34311L9.25657 6.95329L13.7424 3.35032L14.0555 3.74014ZM9.25657 7.34311L6.93067 5.47496L7.24377 5.08514L9.56968 6.95329L9.25657 7.34311ZM6.84452 5.22007C6.89794 5.00391 6.9265 4.7771 6.9265 4.54308H7.4265C7.4265 4.81791 7.39295 5.08498 7.32992 5.34003L6.84452 5.22007ZM6.9265 4.54308C6.9265 3.04991 5.77128 1.85548 4.37674 1.85548V1.35548C6.0698 1.35548 7.4265 2.79669 7.4265 4.54308H6.9265ZM4.37674 1.85548C2.98225 1.85548 1.82702 3.04991 1.82702 4.54308H1.32702C1.32702 2.79669 2.68372 1.35548 4.37674 1.35548V1.85548ZM1.82702 4.54308C1.82702 6.0363 2.98225 7.23073 4.37674 7.23073V7.73073C2.68372 7.73073 1.32702 6.28952 1.32702 4.54308H1.82702ZM4.37674 7.23073C4.87896 7.23073 5.34769 7.07632 5.74406 6.80955L6.02324 7.22436C5.54881 7.54366 4.98367 7.73073 4.37674 7.73073V7.23073ZM6.0402 6.82204L7.88665 8.3051L7.57354 8.69492L5.7271 7.21187L6.0402 6.82204ZM7.88665 8.69493L6.0402 10.178L5.7271 9.78813L7.57354 8.3051L7.88665 8.69493ZM5.74407 10.1905C5.34769 9.9237 4.87896 9.76929 4.37674 9.76929V9.26929C4.98366 9.26929 5.5488 9.45636 6.02323 9.77564L5.74407 10.1905ZM4.37674 9.76929C2.98225 9.76929 1.82702 10.9637 1.82702 12.4569H1.32702C1.32702 10.7105 2.68372 9.26929 4.37674 9.26929V9.76929ZM1.82702 12.4569C1.82702 13.9501 2.98224 15.1445 4.37674 15.1445V15.6445C2.68373 15.6445 1.32702 14.2033 1.32702 12.4569H1.82702ZM4.37674 15.1445C5.77128 15.1445 6.9265 13.9501 6.9265 12.4569H7.4265C7.4265 14.2033 6.06979 15.6445 4.37674 15.6445V15.1445ZM6.9265 12.4569C6.9265 12.2229 6.89794 11.9961 6.84452 11.78L7.32992 11.66C7.39295 11.915 7.4265 12.1821 7.4265 12.4569H6.9265ZM6.93067 11.5251L9.25657 9.65689L9.56968 10.0467L7.24377 11.9149L6.93067 11.5251ZM9.56968 9.65689L14.0555 13.2599L13.7424 13.6497L9.25657 10.0467L9.56968 9.65689ZM14.0555 13.2599C14.7055 13.7819 15.6358 13.6558 16.1382 12.9672L16.5421 13.2619C15.8754 14.1758 14.6197 14.3543 13.7424 13.6497L14.0555 13.2599ZM16.1383 12.9671L16.1685 12.9256L16.5723 13.2205L16.542 13.262L16.1383 12.9671ZM16.1685 12.9257C16.171 12.9223 16.1729 12.9173 16.1729 12.9107H16.6729C16.6729 13.0175 16.6406 13.1269 16.5724 13.2204L16.1685 12.9257ZM16.1729 12.9107C16.1729 12.9059 16.1719 12.9017 16.1704 12.8985C16.1691 12.8956 16.1677 12.8941 16.1665 12.8931L16.4795 12.5032C16.6086 12.6069 16.6729 12.7605 16.6729 12.9107H16.1729ZM16.1664 12.893L10.9396 8.69492L11.2527 8.3051L16.4795 12.5032L16.1664 12.893ZM10.9396 8.3051L16.1664 4.10697L16.4795 4.4968L11.2527 8.69492L10.9396 8.3051ZM16.1664 4.10699C16.1685 4.10532 16.1729 4.09983 16.1729 4.08937L16.6729 4.08924C16.673 4.23962 16.6086 4.39311 16.4795 4.49678L16.1664 4.10699Z"
                fill="#B19499"
              />
            </svg>
          </span>
          <span className="text-uppercase">{t('common:service')}</span>
        </div>
      </DefaultPageLayout.Title>

      <DefaultPageLayout.Sidebar>
        <Row>
          <Col span={18}>
            <Input
              placeholder={t('servicepage.service_search_placeholder')}
              size="large"
              suffix={<img src={SearchIcon} alt="Search Services" />}
              onChange={onSearch}
            />
          </Col>
          <Col span={6}>
            <Button
              size="large"
              className="w-100 border-transparent button-ghost"
              onClick={addService}
              ghost={true}
            >
              <PlusCircleFilled
                twoToneColor="#2776B3"
                style={{ fontSize: '28px' }}
                alt="Add New Service"
              />
            </Button>
          </Col>
        </Row>
        <div className="mt-4"></div>

        <div className="service-list list">
          <List
            dataSource={serviceList}
            renderItem={(item) => (
              <List.Item
                key={`${item.title} ${item.id}`}
                className="text-start appear-effect"
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{ background: '#B19499' }}
                      size="large"
                      shape="circle"
                      src={item.image_url || ServicePlaceholderIcon}
                    />
                  }
                  title={
                    <Link to="#" onClick={() => selectService(item)}>
                      <Space size="small">
                        <svg
                          width="19"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="9.33301"
                            cy="9"
                            r="7.5"
                            fill={item.color || '#D4D3CF'}
                            stroke="#FFFDFD"
                            strokeWidth="3"
                          />
                        </svg>

                        <span className="font-14 text-capitalize text-white">
                          {item.title}
                        </span>
                      </Space>
                    </Link>
                  }
                />
                <span className="service-duration font-12">
                  {item.readableDuration}/{item.price}€
                </span>
              </List.Item>
            )}
          />
        </div>
      </DefaultPageLayout.Sidebar>

      <DefaultPageLayout.Content>
        {mode === INITIAL_MODE && emptyContent}

        <Spin
          spinning={selectorLoadingStatus.includes(
            'fetchServiceDetail',
            'removeService',
            'updateService',
          )}
          style={{ minHeight: '80vh' }}
        >
          {mode !== INITIAL_MODE && (
            <ServiceForm
              staffs={allStaff}
              onDelete={onHandleDeleteService}
              onSubmit={updateOrCreateService}
              service={mode === EDIT_MODE ? service : null}
              hours={range(0, 23)}
              services={services}
            />
          )}
        </Spin>

        <div className="my-4 py-4 w-100"></div>
      </DefaultPageLayout.Content>
    </DefaultPageLayout>
  );
}

export default ServicePage;
